import axios from 'axios';
import DOMPurify from 'dompurify';
import api from './api';

interface CooldownInfo {
  inCooldown: boolean;
  cooldownTimeMs: number;
  maxAttempts: number;
}

class SecurityService {
  private static instance: SecurityService;
  private loginAttempts: number;
  private loginCooldown: boolean;
  private loginCooldownTime: number;
  private maxLoginAttempts: number;
  private csrfToken: string | null;
  private tokenInitializing: Promise<string | null> | null;

  private constructor() {
    this.loginAttempts = 0;
    this.loginCooldown = false;
    this.loginCooldownTime = 30000; // 30 sekundi
    this.maxLoginAttempts = 5;
    this.csrfToken = null;
    this.tokenInitializing = null;
  }

  public static getInstance(): SecurityService {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService();
    }
    return SecurityService.instance;
  }

  sanitizeInput(input: string): string {
    if (typeof input !== 'string') return input;
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
  }

  sanitizeObject(obj: Record<string, any>): Record<string, any> {
    if (!obj || typeof obj !== 'object') return obj;

    const result: Record<string, any> = {};

    for (const key in obj) {
      if (typeof obj[key] === 'string') {
        result[key] = this.sanitizeInput(obj[key]);
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        result[key] = this.sanitizeObject(obj[key]);
      } else {
        result[key] = obj[key];
      }
    }

    return result;
  }

  async refreshCsrfToken(): Promise<string | null> {
    try {
      const response = await api.get('/api/auth/csrf-token');

      if (response.data && response.data.csrfToken) {
        this.csrfToken = response.data.csrfToken;
        return this.csrfToken;
      } else {
        console.error('CSRF token not found in response');
        return null;
      }
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      this.csrfToken = null;
      return null;
    }
  }

  async getCsrfToken(): Promise<string | null> {
    // If token is already set, return it immediately
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // If initialization is already in progress, wait for that promise
    if (this.tokenInitializing) {
      return this.tokenInitializing;
    }

    // Start a new token request
    this.tokenInitializing = this.refreshCsrfToken();

    try {
      const token = await this.tokenInitializing;
      return token;
    } finally {
      // Clear promise reference after completion
      this.tokenInitializing = null;
    }
  }

  trackLoginAttempt(): boolean {
    if (this.loginCooldown) {
      return false;
    }
    this.loginAttempts++;
    if (this.loginAttempts >= this.maxLoginAttempts) {
      this.loginCooldown = true;
      setTimeout(() => {
        this.loginCooldown = false;
        this.loginAttempts = 0;
      }, this.loginCooldownTime);
      return false;
    }
    return true;
  }

  resetLoginAttempts(): void {
    this.loginAttempts = 0;
    this.loginCooldown = false;
  }

  getRemainingLoginAttempts(): number {
    return this.maxLoginAttempts - this.loginAttempts;
  }

  getCooldownInfo(): CooldownInfo {
    return {
      inCooldown: this.loginCooldown,
      cooldownTimeMs: this.loginCooldownTime,
      maxAttempts: this.maxLoginAttempts,
    };
  }
}

// Create singleton instance
const securityService = SecurityService.getInstance();

export default securityService;
